import { deepFreeze } from '@rivethealth/core/object';
import { Id } from './id';
import {
  hubspotEquivalents,
  HubspotProductTierName,
  PermissionedProductTierName,
  ProductName,
  productNames,
} from './rivet-product';

export type Twilio = {
  twilioBrandSid: string | null;
  twilioBrandType: string;
  businessProfileStatus: string;
  trustBundleStatus: string;
  brandRegistrationStatus: string;
  messagingServiceStatus: string | null;
  campaignStatus: string | null;
  ready: boolean;
  failureReason: string | unknown;
};

export type CompanyType = {
  type: 'public' | 'private' | 'government' | 'non-profit';
  stockExchange?: 'NYSE' | 'NASDAQ';
  stockTicker?: string;
};

export type Customer = {
  id?: string;
  accountNumber?: number; // i.e. an external reference to the Rivet Customer, e.g. in Hubspot
  legalCompanyName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  location: {
    address1: string;
    address2?: string;
    city: string;
    state: string;
    zip: string;
  };
  EIN: string;
  companyType: CompanyType;
  jobPosition:
    | 'Director'
    | 'GM'
    | 'VP'
    | 'CEO'
    | 'CFO'
    | 'General_Counsel'
    | 'Other';
  businessType:
    | 'Sole Prprietorship'
    | 'Partnership'
    | 'Limited Liability Corportation'
    | 'Co-operative'
    | 'Non-profit Corporation'
    | 'Corporation';
  websiteUrl: String;
  twilio?: Twilio;
};

export type Hubspot = {
  status: string; // either IM Management or CSM Management
  purchased?: PurchasedProducts_v2;
};

export type Products = {
  permissioned?: PermissionedProducts;
};

export type Account = {
  name: string;
  subscription: {
    start: string;
    end: string;
  };
  efax: string;
  internal: boolean;
  claimsyEnabled: boolean;
  hasPaymentCredential: boolean;
  paymentCredentialType?: 'gpi' | 'rectangle';
  isActive?: boolean;
  customer: Customer | null;
  hubspot: Hubspot;
  eligCheckQuota: number | null;
  trucodeCustomerId: string | null;
  hospitalEstimatesEnabled: boolean;
  markedExpiredDate?: string;
  scheduledDeletionDate?: string;
  products: Products;
};

export type RouterAccount = {
  id: number;
  name: string;
  cell: string;
};

export type ZelisRouterAccount = RouterAccount & {
  zelisProviderId: string;
  providerRef: string;
  tin: string;
  aggregateAccountId: number | null;
};

export type ZelisAccount = {
  id: number;
  name: string;
  internal: boolean;
};

export type GetZelisAccount = {
  id: number;
  name: string;
  externalProviderRef: string;
};

export type ExistingAccount = Id & Account;

export type AccountPaymentCredential =
  | null
  | {
      type: 'gpi';
      credential: { credential: string };
    }
  | {
      type: 'rectangle';
      credential: {
        orgId: string;
        mac: string;
        publicKey: string;
        username: string;
        password: string;
        paymentUrl?: string;
        privateKey?: string;
        walletSite?: string;
      };
    };

export type AccountType = 'customer' | 'internal' | 'expired';

export type ServiceProviderSettings = {
  entityId: string;
  assertionConsumerServiceUrl: string;
};

export type IdentityProviderSettings = {
  entityId: string;
  singleSignOnServiceUrl: string;
  certificate: string;
};

export type SsoSettings = {
  serviceProvider: ServiceProviderSettings;
  identityProvider: IdentityProviderSettings | null;
};

export type AccountSummary = {
  name: string;
  contractCount: number;
  payerCount: number;
};

export type AccountDenialsRoi = {
  worked: number;
  recoveredQuarter: number;
  recoveredYear: number;
};

export type PhiConsentExpiration = {
  days: number;
};

export type PhiChallengeFactor =
  | 'date_of_birth'
  | 'last_name'
  | 'ssn_last_four';

export enum AccountClaimsMonitoringStatus {
  green = 0,
  yellow = 1,
  red = 2,
}

export type ProductPermissions =
  | 'underpayments_view'
  | 'claims_analytics_view'
  | 'denials_worklist_view';

export interface AccountClaimsMonitoring {
  account_id: number;
  account_name: string;
  active: 'active' | 'inactive';
  has_claims: boolean;
  has_remits: boolean;
  '835_match_rate_12_mons': number;
  '837_match_rate_12_mons': number;
  '835_match_rate_3_mons': number;
  '837_match_rate_3_mons': number;
  last_remit_import: Date;
  last_claim_import: Date;
  remit_alert_threshold: number;
  claim_alert_threshold: number;
  min_remit_date: Date;
  max_remit_date: Date;
  min_claim_date: Date;
  max_claim_date: Date;
  remit_count_12_mons: number;
  claim_count_12_mons: number;
  remit_count_3_mons: number;
  claim_count_3_mons: number;
  remit_rate_change: number;
  claim_rate_change: number;
  line_item_match_rate_3_mons: number;
  line_item_match_rate_12_mons: number;
  status: AccountClaimsMonitoringStatus;
  reasons: string[];
  last_activity?: Date;
  permissions: ProductPermissions[];
  denials: string;
  underpayments: string;
  friendly_type: 'Claim SFTP' | 'SFTP Pull';
}

export interface AccountClaimsMonitoringMonth {
  year_month: string;
  '837_match_rate': number;
  '835_match_rate': number;
  '837_count': number;
  '835_count': number;
  line_item_match_rate: number;
  error_rate: number;
}

export interface AccountClaimsMonitoringDetail {
  account_id: number;
  account_name: string;
  entries: AccountClaimsMonitoringMonth[];
}

export type AccountClaimsMonitoringFilters = {
  account: string[];
  status: AccountClaimsMonitoringStatus[];
};

export type AccountClaimImportsTaskState = {
  claimFileImport: {
    pending: number;
    error: number;
  };
  claimFilePartImport: {
    pending: number;
    error: number;
  };
  claimFilePartImportDL: {
    pending: number;
    error: number;
  };
};

export type AccountClaimFileImportSummary = {
  dateCreated: string;
  totalClaimFiles: number;
  countClaimFilesWithError: number;
  countClaimFilesWithClaims: number;
  countClaimFilesWithRemits: number;
  sumClaimFilePartsWithClaims: number;
  sumClaimFilePartsWithRemits: number;
  listTaxIds: string[];
};

export type AccountClaimFileImportErrorPreview = {
  dateCreated: Date;
  filename: string;
  fileType: string;
  countClaimFileParts: number;
  errorPreview: string;
};

export type AccountClaimFilePartStateSummary = {
  taxId: string;
  countError: number;
  countQuarantined: number;
  countNotStarted: number;
  countOther: number;
};

export type AccountClaimFileImportMonitoringSummary = {
  accountClaimImportsTaskState: AccountClaimImportsTaskState;
  accountClaimFileImportSummary: AccountClaimFileImportSummary[];
  accountClaimFileImportErrorPreview: AccountClaimFileImportErrorPreview[];
  accountClaimFilePartStateSummary: AccountClaimFilePartStateSummary[];
};

export type ClaimFilesInspectSummary = {
  id: string;
  name: string;
  created: Date;
  s3Key: string;
  taxIds: string[];
  fileType: string;
  size: number;
  partsCount: number;
  errorPreview: string;
};

export type PurchasedProducts_v2 = {
  [Product in ProductName]: {
    tier: HubspotProductTierName | null;
    units: number | null;
  };
};

export const nullPurchasedProducts: {
  [Product in ProductName]: { tier: null; units: null };
} = deepFreeze(
  Object.fromEntries(
    productNames.map(productName => [productName, { tier: null, units: null }]),
  ),
) as any;

export type TierImplementationCompleteDates = {
  [Tier in HubspotProductTierName]?: string | null;
};

export const nullTierImplementationCompleteDates: {
  [Tier in HubspotProductTierName]: null;
} = deepFreeze(
  Object.fromEntries(
    Object.keys(hubspotEquivalents).map(tierName => [tierName, null]),
  ),
) as any;

export type PermissionedProducts = {
  [Product in ProductName]?: {
    tier: PermissionedProductTierName | null;
  };
};

export type ExpectedRateTypes = {
  facility: boolean;
  nonFacility: boolean;
  asc: boolean;
};

export type ExpectedClaimTypes = {
  claim: boolean;
  remit: boolean;
};

export type DataPoint<T> = {
  date: string;
  value: T;
};

export type Series<T> = {
  label: string;
  points: DataPoint<T>[];
};

export type ClaimsTimeseries = {
  start: string;
  end: string;
  series: Series<{ count: number }>[];
};

export type ClaimRemitStatus = {
  remitMatchRateAllTime: number;
  claimMatchRateAllTime: number;
  remitMatchRate3Months: number;
  claimMatchRate3Months: number;
  remitLastImport: string;
  claimLastImport: string;
  remitCountAllTime: number;
  claimCountAllTime: number;
  remitCount30Days: number;
  claimCount30Days: number;
  lastActivity: string;
  remitAlertThreshold: number;
  claimAlertThreshold: number;
};

export type ScheduleCoverage = {
  scheduleId: number;
  cptGroupSetKey: string;
  payerId: number;
  name: string;
  facilityType: string;
  overlapCount: number;
  overlapPercent: number;
};

export type ChargemasterFeeScheduleCoverage = {
  chargemasterCodeCounts: {
    facilityType: string;
    valid: number;
    total: number;
  }[];
  scheduleCoverages: ScheduleCoverage[];
};

export const emptyHubspot: Hubspot = {
  status: '',
};

export const emptyExistingAccount: ExistingAccount = {
  id: -1,
  name: '',
  subscription: {
    start: '',
    end: '',
  },
  efax: '',
  internal: false,
  claimsyEnabled: false,
  hasPaymentCredential: false,
  isActive: true,
  customer: null,
  hubspot: emptyHubspot,
  eligCheckQuota: null,
  trucodeCustomerId: null,
  hospitalEstimatesEnabled: false,
  products: {},
};

export interface ProductTicket {
  id: string;
  title: string;
  status: string;
}

export type ClaimsRoutingRule = {
  id: number;
  taxId: string;
  incomingAccountId: number;
  incomingAccountName: string;
  targetAccountId: number;
  targetAccountName: string;
  targetDataSourceName: string;
};
